import React, { useCallback, useMemo } from 'react';
import { useGetGlobalAttributes, useGetXstyledProps } from '@elliemae/ds-props-helpers';
import { uid } from 'uid';
import { useGetDatePickerWithDefaultsProps } from './useGetPropsWithDefault.js';
import { useFocusTracker } from './useFocusTracker.js';
import { useGetFlags } from './useGetFlags.js';
import { useGetReferences } from './useGetReferences.js';
import { useRelevantValueFromProps } from './useRelevantValueFromProps.js';
import { useGetPropsBasedOnType } from './useGetPropsBasedOnType.js';
import { useGetDestructuredValues } from './useGetDestructuredValues.js';
import { useChangeHandlers } from './useChangeHandlers.js';
import { useGlobalKeyHandlers } from './useGlobalKeyHandlers.js';
import { useValidateProps } from './useValidateProps.js';
import type { DSControlledDateTimePickerT } from '../react-desc-prop-types.js';

export type ControlledDateTimePickerContextT = ReturnType<typeof useGetFlags> &
  ReturnType<typeof useFocusTracker> &
  ReturnType<typeof useGetReferences> &
  ReturnType<typeof useGetPropsBasedOnType> &
  ReturnType<typeof useRelevantValueFromProps> &
  ReturnType<typeof useGetDestructuredValues> &
  ReturnType<typeof useChangeHandlers> &
  ReturnType<typeof useGlobalKeyHandlers> & {
    props: DSControlledDateTimePickerT.InternalProps;
    getProps: () => DSControlledDateTimePickerT.InternalProps;
    ariaCurrentValueForInputs: string;
    globalAttrs: Record<string, unknown>;
    xstyledAttrs: Record<string, unknown>;
    instanceUid: string;
  };

export const useControlledDateTimePicker = (
  props: DSControlledDateTimePickerT.Props,
): ControlledDateTimePickerContextT => {
  // if we detect wrong props configuration we throw meaningful errors
  useValidateProps(props);

  const instanceUid = useMemo(() => `ds-dtp-${uid(6)}`, []);
  const propsWithDefaults = useGetDatePickerWithDefaultsProps(props);

  const flags = useGetFlags(propsWithDefaults);
  const DOMRefs = useGetReferences(flags); // refs are used for "auto-advance" feature
  const focusTrackers = useFocusTracker({ DOMRefs, flags });
  const relevantValues = useRelevantValueFromProps(propsWithDefaults);
  const propsBasedOnType = useGetPropsBasedOnType(propsWithDefaults);
  const destructuredValues = useGetDestructuredValues({ ...propsBasedOnType, ...flags });
  const changeHandlers = useChangeHandlers({
    relevantValues,
    destructuredValues,
    propsBasedOnType,
    DOMRefs,
    focusTrackers,
  });

  const globalKeyHandlers = useGlobalKeyHandlers({ changeHandlers, propsBasedOnType, DOMRefs, flags, focusTrackers });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  // const dataAttrs = getDataProps<DSControlledDateTimePickerT.InternalProps>(propsWithDefaults);

  const xstyledAttrs = useGetXstyledProps(propsWithDefaults) as Record<string, unknown>;

  const globalAttrs = useGetGlobalAttributes(propsWithDefaults) as Record<string, unknown>;

  const { announcedDateValues, announcedTimeValues } = destructuredValues;
  const { isWithDateInputs, isDateTimeSelector, isWithTimeInputs } = flags;

  const ariaCurrentValueForInputs = `current values: ${isWithDateInputs ? `${announcedDateValues}` : ''}${
    isDateTimeSelector ? ', ' : ''
  }${isWithTimeInputs ? `${announcedTimeValues}` : ''}`;

  const getProps = useCallback(() => propsWithDefaults, [propsWithDefaults]);

  const ctx = React.useMemo(
    () => ({
      props: propsWithDefaults,
      getProps,
      ariaCurrentValueForInputs,
      instanceUid,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      xstyledAttrs,
      globalAttrs,
      ...focusTrackers,
      ...flags,
      ...DOMRefs,
      ...relevantValues,
      ...propsBasedOnType,
      ...destructuredValues,
      ...changeHandlers,
      ...globalKeyHandlers,
    }),
    [
      propsWithDefaults,
      getProps,
      ariaCurrentValueForInputs,
      instanceUid,
      xstyledAttrs,
      globalAttrs,
      focusTrackers,
      flags,
      DOMRefs,
      relevantValues,
      propsBasedOnType,
      destructuredValues,
      changeHandlers,
      globalKeyHandlers,
    ],
  );

  return ctx;
};
